import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AccionesRol, FuncionesRol, IRol } from 'modelos/src';
import { firstValueFrom } from 'rxjs';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { RolsService } from '../roles.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { TODAS_LAS_ACCIONES, TODAS_LAS_FUNCIONES } from '../roles.constants';
import { LoginService } from '../../../../login/login.service';

interface AccionesPorModulo {
  modulo: string;
  acciones: AccionesRol[];
  seleccionadas: Set<AccionesRol>;
}

@Component({
  selector: 'app-crear-editar-rol',
  templateUrl: './crear-editar-rol.component.html',
  styleUrl: './crear-editar-rol.component.scss',
  standalone: false,
})
export class CrearEditarRolsComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private lista2 = inject(Lista2Service);
  helper = inject(HelperService);
  private service = inject(RolsService);

  public loading = false;
  public form?: FormGroup;
  public title?: string;
  public subtitle?: string;

  public id?: string | null;
  public rol?: IRol;
  public esClienteNivel0 = false;

  // Acciones que el usuario logueado posee (para restringir la vista)
  public accionesPropiasUsuario = new Set<AccionesRol>();

  // Acciones agrupadas por módulo
  public accionesPorModulo: AccionesPorModulo[] = [];

  // Todas las acciones disponibles (importadas desde constante compartida)
  public readonly TODAS_LAS_ACCIONES = TODAS_LAS_ACCIONES;

  // Todas las funciones disponibles
  public readonly TODAS_LAS_FUNCIONES = TODAS_LAS_FUNCIONES;

  private createForm() {
    // Solo permitir global si el usuario es nivel 0
    const globalValue = this.esClienteNivel0
      ? this.rol?.global || false
      : false;

    this.form = new FormGroup({
      nombre: new FormControl(this.rol?.nombre, [Validators.required]),
      global: new FormControl(globalValue, Validators.required),
      acciones: new FormControl(this.rol?.acciones || [], [
        Validators.required,
      ]),
      funciones: new FormControl(this.rol?.funciones || []),
    });
  }

  private calcularAccionesPropiasUsuario() {
    // Si es nivel 0, tiene acceso a todas las acciones
    if (this.esClienteNivel0) {
      TODAS_LAS_ACCIONES.forEach((a) => this.accionesPropiasUsuario.add(a));
      return;
    }
    // Obtener las acciones del permiso activo del usuario logueado
    const permiso = LoginService.getPermisoActual();
    permiso?.rols?.forEach((rol) => {
      rol.acciones?.forEach((accion) =>
        this.accionesPropiasUsuario.add(accion),
      );
    });
  }

  private agruparAccionesPorModulo() {
    // Filtrar acciones a las que el usuario tiene acceso
    const accionesDisponibles = TODAS_LAS_ACCIONES.filter((a) =>
      this.accionesPropiasUsuario.has(a),
    );

    const grupos = new Map<string, AccionesRol[]>();

    // Agrupar acciones por módulo
    accionesDisponibles.forEach((accion) => {
      const modulo = accion.split(' - ')[0].trim();
      if (!grupos.has(modulo)) {
        grupos.set(modulo, []);
      }
      grupos.get(modulo)!.push(accion);
    });

    // Convertir a array y ordenar por nombre de módulo
    this.accionesPorModulo = Array.from(grupos.entries())
      .map(([modulo, acciones]) => ({
        modulo,
        acciones,
        seleccionadas: new Set(
          this.rol?.acciones?.filter((a) => acciones.includes(a)) || [],
        ),
      }))
      .sort((a, b) => a.modulo.localeCompare(b.modulo));
  }

  public isAccionSeleccionada(accion: AccionesRol): boolean {
    const acciones = this.form?.get('acciones')?.value || [];
    return acciones.includes(accion);
  }

  public toggleAccion(accion: AccionesRol) {
    const accionesControl = this.form?.get('acciones');
    if (!accionesControl) return;

    const acciones: AccionesRol[] = [...accionesControl.value];
    const index = acciones.indexOf(accion);

    if (index > -1) {
      acciones.splice(index, 1);
    } else {
      acciones.push(accion);
    }

    accionesControl.setValue(acciones);
    accionesControl.markAsDirty();
  }

  public toggleModulo(modulo: AccionesPorModulo, event: any) {
    const accionesControl = this.form?.get('acciones');
    if (!accionesControl) return;

    const acciones: AccionesRol[] = [...accionesControl.value];
    const todasSeleccionadas = modulo.acciones.every((a) =>
      acciones.includes(a),
    );

    if (todasSeleccionadas) {
      // Deseleccionar todas las acciones del módulo
      modulo.acciones.forEach((accion) => {
        const index = acciones.indexOf(accion);
        if (index > -1) {
          acciones.splice(index, 1);
        }
      });
    } else {
      // Seleccionar todas las acciones del módulo
      modulo.acciones.forEach((accion) => {
        if (!acciones.includes(accion)) {
          acciones.push(accion);
        }
      });
    }

    accionesControl.setValue(acciones);
    accionesControl.markAsDirty();
  }

  public isModuloCompleto(modulo: AccionesPorModulo): boolean {
    const acciones = this.form?.get('acciones')?.value || [];
    return modulo.acciones.every((a) => acciones.includes(a));
  }

  public isModuloParcial(modulo: AccionesPorModulo): boolean {
    const acciones = this.form?.get('acciones')?.value || [];
    const seleccionadas = modulo.acciones.filter((a) => acciones.includes(a));
    return (
      seleccionadas.length > 0 && seleccionadas.length < modulo.acciones.length
    );
  }

  public getCantidadSeleccionadas(modulo: AccionesPorModulo): number {
    const acciones = this.form?.get('acciones')?.value || [];
    return modulo.acciones.filter((a) => acciones.includes(a)).length;
  }

  // Métodos para manejar funciones
  public isFuncionSeleccionada(funcion: FuncionesRol): boolean {
    const funciones = this.form?.get('funciones')?.value || [];
    return funciones.includes(funcion);
  }

  public toggleFuncion(funcion: FuncionesRol) {
    const funcionesControl = this.form?.get('funciones');
    if (!funcionesControl) return;

    const funciones: FuncionesRol[] = funcionesControl.value || [];
    if (funciones.includes(funcion)) {
      // Deseleccionar
      funcionesControl.setValue([]);
    } else {
      // Seleccionar solo esta función
      funcionesControl.setValue([funcion]);
    }
    funcionesControl.markAsDirty();
  }

  private async listar() {
    this.rol = await firstValueFrom(this.lista2.rol(this.id!));
  }

  public async onSubmit() {
    try {
      const data = this.form?.value;
      if (this.id) {
        // Update
        await this.service.update(this.id, data);
        this.helper.notifSuccess('Rol actualizado');
      } else {
        // Create
        await this.service.create(data);
        this.helper.notifSuccess('Rol creado');
      }
      HelperService.volver();
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
  }

  public volver() {
    HelperService.volver();
  }

  private setTitles() {
    if (this.rol) {
      this.title = 'Editar Rol';
      this.subtitle = this.rol?.nombre;
    } else {
      this.title = 'Crear Rol';
    }
  }

  /////// HOOKS

  async ngOnInit() {
    this.loading = true;
    const params = await firstValueFrom(this.route.paramMap);
    this.id = params.get('id');
    this.esClienteNivel0 = this.helper.esNivel0();
    this.calcularAccionesPropiasUsuario();
    if (this.id) {
      await this.listar();
    }
    this.agruparAccionesPorModulo();
    this.setTitles();
    this.createForm();
    this.loading = false;
  }
}
